import React, { useState, useRef, useEffect } from 'react';
import { IoCalendarOutline, IoTimeOutline, IoCameraOutline, IoChevronDown } from 'react-icons/io5';
import { FaThermometerHalf } from 'react-icons/fa';
import { FiCheckCircle, FiChevronDown, FiUser } from 'react-icons/fi';
import { useFoodTemperature } from '../context/FoodTemperatureContext';

export default function AddTemperatureEntryForm() {
  const {
    selectedDate: date, setSelectedDate: setDate, entry, updateEntry,
    staff: users, staffLoading, staffError, selectedUser, selectUser,
    loading, error, isSaving, clearEntry, addEntry,
  } = useFoodTemperature();
  const { time, foodItemName, type, initialTemp, finalTemp, actionTaken, comments, photo } = entry;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!photo && fileInputRef.current) fileInputRef.current.value = '';
  }, [photo, date]);
  useEffect(() => { setIsDropdownOpen(false); }, [date]);

  const handleSelectUser = user => {
    setIsDropdownOpen(false);
    selectUser(user);
  };
  const handleClear = () => {
    clearEntry();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleSubmit = async e => {
    e.preventDefault();
    await addEntry();
  };

  return (
    <div className="w-full  p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
      
      <div className="flex items-center justify-between px-2 pt-2 border-b border-slate-100 pb-2">
        <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Opening Check Items</h3>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            disabled={loading || isSaving || staffLoading}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`w-full flex items-center justify-between px-3 py-2 bg-white border rounded-lg text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${isDropdownOpen ? 'border-blue-500 shadow-sm' : 'border-slate-200 hover:border-slate-300'
              }`}
          >
            <div className="flex items-center gap-2 text-slate-700">
              <FiUser className={selectedUser ? 'text-blue-500' : 'text-slate-400'} />
              <span className={selectedUser ? 'font-medium text-slate-900' : 'text-slate-400'}>
                {selectedUser ? selectedUser.fullName : 'Select Staff Member...'}
              </span>
            </div>
            <FiChevronDown className={`text-slate-400 transition-transform duration-150 ${isDropdownOpen ? 'rotate-180 text-blue-500' : ''}`} />
          </button>

          {isDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto py-1">
              {users && users.length > 0 ? (
                users.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      handleSelectUser(user);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${selectedUser?.id === user.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    <span className="truncate">{user.fullName}</span>
                    {selectedUser?.id === user.id && <FiCheckCircle className="text-blue-500 shrink-0 ml-2" />}
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-xs text-center text-slate-400 italic">No users found</div>
              )}
            </div>
          )}
        </div>
      </div>

      {(error || staffError) && <p role="alert" className="mt-3 text-sm text-red-600">{error || staffError}</p>}
      {loading && <p role="status" className="mt-3 text-sm text-gray-500">Loading temperature records...</p>}

      {/* Header Row */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-gray-900">Add Temperature Entry</h2>
        <button
          type="button"
          onClick={handleClear}
          className="px-4 py-2 text-sm font-semibold text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Clear
        </button>
      </div>

      {/* Form Element */}
   <form onSubmit={handleSubmit}>
      <fieldset disabled={isSaving || loading} className="flex flex-col gap-5">
        {/* Row 1: Date, Time, Food Item, Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* Date Input */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500">Date</label>
            <div className="relative">
              <input
                aria-label="Entry date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full pl-3 pr-10 py-2.5 text-sm font-medium text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white transition-all"
              />
              <IoCalendarOutline className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" />
            </div>
          </div>

          {/* Time Input */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500">Time</label>
            <div className="relative">
              <input
                aria-label="Entry time"
                required
                type="time"
                value={time}
                onChange={(e) => updateEntry('time', e.target.value)}
                className="w-full pl-3 pr-10 py-2.5 text-sm font-medium text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white transition-all"
              />
              <IoTimeOutline className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" />
            </div>
          </div>

          {/* Food Item Dropdown */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500">Food Item</label>
            <div className="relative">
              <input
                type="text"
                value={foodItemName}
                onChange={(e) => updateEntry('foodItemName', e.target.value)}
                aria-label="Food item"
                required
                placeholder="e.g. Chicken curry"
                className="w-full px-3 py-2.5 text-sm font-medium text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl placeholder-gray-300 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all"
              />
              <IoChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />
            </div>
          </div>

          {/* Type Dropdown */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500">Type</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-rose-500 pointer-events-none">
                <FaThermometerHalf className="text-sm" />
              </span>
              <select
                value={type}
                onChange={(e) => updateEntry('type', e.target.value)}
                className="w-full pl-9 pr-10 py-2.5 text-sm font-semibold text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl appearance-none focus:outline-none focus:border-indigo-500 focus:bg-white transition-all"
              >
                <option value="Hot Holding">Hot Holding</option>
                <option value="Cold Holding">Cold Holding</option>
              </select>
              <IoChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />
            </div>
          </div>

        </div>

        {/* Row 2: Initial Temp, Final Temp, Action Taken, Comments */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* Initial Temperature */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500">Initial Temperature (°C)</label>
            <input
              type="number"
              value={initialTemp}
              onChange={(e) => updateEntry('initialTemp', e.target.value)}
              step="any"
              placeholder="e.g. 65"
              className="w-full px-3 py-2.5 text-sm font-medium text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl placeholder-gray-300 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all"
            />
          </div>

          {/* Final Temperature */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500">Final Temperature (°C)</label>
            <input
              type="number"
              value={finalTemp}
              onChange={(e) => updateEntry('finalTemp', e.target.value)}
              step="any"
              placeholder="e.g. 63"
              className="w-full px-3 py-2.5 text-sm font-medium text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl placeholder-gray-300 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all"
            />
          </div>

          {/* Action Taken Area */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500">Action Taken</label>
            <textarea
              rows="1"
              value={actionTaken}
              onChange={(e) => updateEntry('actionTaken', e.target.value)}
              placeholder="e.g. Adjusted heating"
              className="w-full px-3 py-2.5 text-sm font-medium text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl placeholder-gray-300 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all resize-y min-h-[42px]"
            />
          </div>

          {/* Comments Area */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500">Comments <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              rows="1"
              value={comments}
              onChange={(e) => updateEntry('comments', e.target.value)}
              placeholder="Add any notes"
              className="w-full px-3 py-2.5 text-sm font-medium text-gray-800 bg-gray-50/50 border border-gray-200 rounded-xl placeholder-gray-300 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all resize-y min-h-[42px]"
            />
          </div>

        </div>

        {/* Row 3: Photo & Submit Button */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-2">
          
          {/* Photo Upload Zone */}
          <div className="flex flex-col gap-1.5 w-full sm:max-w-xs">
            <label className="text-xs font-semibold text-gray-500">Photo <span className="text-gray-400 font-normal">(optional)</span></label>
            <label className="flex flex-col items-center justify-center w-full h-24 border border-dashed border-gray-300 rounded-xl bg-gray-50/30 hover:bg-gray-50 cursor-pointer transition-all">
              <div className="flex flex-col items-center gap-1.5 text-center px-4">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <IoCameraOutline className="text-lg text-gray-500" />
                  <span>Upload photo</span>
                </div>
                <span className="text-[10px] font-medium text-gray-400 tracking-wide uppercase">
                  JPG, PNG or WEBP (Max 5MB)
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => updateEntry('photo', e.target.files[0] || null)}
              />
            </label>
          </div>

          {/* Add Entry Button */}
          <button 
            type="submit" 
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-xl shadow-sm hover:bg-emerald-700 active:bg-emerald-800 transition-colors self-end"
          >
            {isSaving ? 'Saving...' : 'Add Entry'}
          </button>

        </div>

      </fieldset>
      </form>
    </div>
  );
}

